import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "contact-form-button" }

import { computed, nextTick, reactive, ref, watch } from 'vue';
import { Helper } from '@src/store/util';
import { Validity } from '@src/view_models/Validity';
import type { ContactFormValidation } from '@src/view_models/Validation';
import { trackFormSubmission } from '@src/util/tracking';
import TextField from '@src/components/shared/form_fields/TextField.vue';
import EmailField from '@src/components/shared/form_fields/EmailField.vue';
import SelectField from '@src/components/shared/form_fields/SelectField.vue';
import FormButton from '@src/components/shared/form_elements/FormButton.vue';
import type { ContactInitialFormData } from '@src/components/pages/contact/ContactInitialFormData';
import type { ContactFormData } from '@src/components/pages/contact/ContactFormData';
import ErrorSummary from '@src/components/shared/ErrorSummary.vue';
import { useI18n } from 'vue-i18n';
import type { ValidationSummaryItem } from '@src/Domain/Validation/ValidationSummaryItem';
import ContentCard from '@src/components/patterns/ContentCard.vue';

interface Props {
	contactCategories: Record<string, string>;
	initialFormData?: ContactInitialFormData;
	errors?: Record<string, string>;
	validationPatterns: ContactFormValidation;
}


export default /*@__PURE__*/_defineComponent({
  ...{
	name: 'Contact',
},
  __name: 'Contact',
  props: {
    contactCategories: {},
    initialFormData: {},
    errors: {},
    validationPatterns: {}
  },
  setup(__props: any) {



const props = __props;
const { t } = useI18n();

const showServerErrorSummary = ref<boolean>( props.errors !== undefined );
const showErrorSummary = ref<boolean>( false );
const form = ref<HTMLFormElement>( null );
const formData = reactive<ContactFormData>( {
	firstname: {
		name: 'name',
		value: props.initialFormData?.firstname ?? '',
		pattern: props.validationPatterns.firstName,
		optionalField: true,
		validity: Validity.VALID,
	},
	lastname: {
		name: 'lastname',
		value: props.initialFormData?.lastname ?? '',
		pattern: props.validationPatterns.lastName,
		optionalField: true,
		validity: Validity.VALID,
	},
	donationNumber: {
		name: 'donationNumber',
		value: props.initialFormData?.donationNumber ?? '',
		pattern: props.validationPatterns.donationNumber,
		optionalField: true,
		validity: props.errors?.donationNumber ? Validity.INVALID : Validity.VALID,
	},
	email: {
		name: 'email',
		value: props.initialFormData?.email ?? '',
		pattern: props.validationPatterns.email,
		optionalField: false,
		validity: props.errors?.email ? Validity.INVALID : Validity.INCOMPLETE,
	},
	topic: {
		name: 'topic',
		value: props.initialFormData?.category ? props.initialFormData?.category : '',
		pattern: props.validationPatterns.topic,
		optionalField: false,
		validity: props.errors?.category ? Validity.INVALID : Validity.INCOMPLETE,
	},
	subject: {
		name: 'subject',
		value: props.initialFormData?.subject ?? '',
		pattern: props.validationPatterns.subject,
		optionalField: false,
		validity: props.errors?.subject ? Validity.INVALID : Validity.INCOMPLETE,
	},
	comment: {
		name: 'comment',
		value: props.initialFormData?.messageBody ?? '',
		pattern: props.validationPatterns.comment,
		optionalField: false,
		validity: props.errors?.messageBody ? Validity.INVALID : Validity.INCOMPLETE,
	},
} );

const validationItems = computed<ValidationSummaryItem[]>( () => [
	{
		validity: formData.donationNumber.validity,
		message: t( 'contact_form_donation_number_error' ),
		focusElement: 'donationNumber',
		scrollElement: 'contact-form-donationNumber',
	},
	{
		validity: formData.email.validity,
		message: t( 'contact_form_email_error' ),
		focusElement: 'email',
		scrollElement: 'address-form-email',
	},
	{
		validity: formData.topic.validity,
		message: t( 'contact_form_topic_error' ),
		focusElement: 'topic',
		scrollElement: 'contact-form-topic',
	},
	{
		validity: formData.subject.validity,
		message: t( 'contact_form_subject_error' ),
		focusElement: 'subject',
		scrollElement: 'contact-form-subject',
	},
	{
		validity: formData.comment.validity,
		message: t( 'contact_form_body_error' ),
		focusElement: 'messageBody',
		scrollElement: 'contact-form-messageBody',
	},
] );

const validateField = ( fieldName: string ): boolean => {
	const field = formData[ fieldName ];
	field.validity = Helper.inputIsValid( field.value, field.pattern, field.optionalField );
	return field.validity === Validity.VALID;
};

const submit = async (): Promise<void> => {
	let isValid = true;
	showErrorSummary.value = false;

	Object.keys( formData ).forEach( ( fieldName: string ) => {
		if ( !validateField( fieldName ) ) {
			isValid = false;
		}
	} );
	if ( isValid ) {
		trackFormSubmission( form.value );
		form.value.submit();
	} else {
		await nextTick();
		showErrorSummary.value = true;
	}
};

watch( formData, ( newFormData: ContactFormData ) => {
	if ( !showErrorSummary.value && !showServerErrorSummary.value ) {
		return;
	}

	if ( newFormData.email.validity === Validity.VALID
		&& newFormData.topic.validity === Validity.VALID
		&& newFormData.subject.validity === Validity.VALID
		&& newFormData.comment.validity === Validity.VALID ) {
		showErrorSummary.value = false;
		showServerErrorSummary.value = false;
	}
} );


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ContentCard, null, {
    heading: _withCtx(() => [
      _createElementVNode("h1", null, _toDisplayString(_ctx.$t( 'contact_form_title' )), 1)
    ]),
    content: _withCtx(() => [
      _createVNode(ErrorSummary, {
        id: "server-error-summary",
        "is-visible": showServerErrorSummary.value,
        items: validationItems.value,
        "id-namespace": "server-",
        "focus-on-submit": false
      }, null, 8, ["is-visible", "items"]),
      _createVNode(ErrorSummary, {
        "is-visible": showErrorSummary.value,
        items: validationItems.value
      }, null, 8, ["is-visible", "items"]),
      _createElementVNode("form", {
        method: "post",
        action: "/contact/get-in-touch",
        onSubmit: _withModifiers(submit, ["prevent"]),
        id: "laika-contact",
        class: "flow",
        ref_key: "form",
        ref: form
      }, [
        _createVNode(TextField, {
          name: "firstname",
          id: "contact-form-firstname",
          "input-id": "firstname",
          modelValue: formData.firstname.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.firstname.value) = $event)),
          label: _ctx.$t( 'contact_form_firstname_label' ),
          "label-help-text": _ctx.$t('contact_form_optional'),
          placeholder: _ctx.$t( 'contact_form_firstname_placeholder' ),
          "show-error": formData.firstname.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_firstname_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[1] || (_cache[1] = () => validateField( 'firstname' ))
        }, null, 8, ["modelValue", "label", "label-help-text", "placeholder", "show-error", "error-message"]),
        _createVNode(TextField, {
          name: "lastname",
          id: "contact-form-lastname",
          "input-id": "lastname",
          modelValue: formData.lastname.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.lastname.value) = $event)),
          label: _ctx.$t( 'contact_form_lastname_label' ),
          "label-help-text": _ctx.$t('contact_form_optional'),
          placeholder: _ctx.$t( 'contact_form_lastname_placeholder' ),
          "show-error": formData.lastname.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_lastname_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[3] || (_cache[3] = () => validateField( 'lastname' ))
        }, null, 8, ["modelValue", "label", "label-help-text", "placeholder", "show-error", "error-message"]),
        _createVNode(TextField, {
          name: "donationNumber",
          id: "contact-form-donationNumber",
          "input-id": "donationNumber",
          modelValue: formData.donationNumber.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.donationNumber.value) = $event)),
          label: _ctx.$t( 'contact_form_donation_number_label' ),
          "label-help-text": _ctx.$t('contact_form_optional' ),
          "help-text": _ctx.$t( 'contact_form_donation_number_help_text' ),
          placeholder: _ctx.$t( 'contact_form_donation_number_placeholder' ),
          "show-error": formData.donationNumber.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_donation_number_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[5] || (_cache[5] = () => validateField( 'donationNumber' ))
        }, null, 8, ["modelValue", "label", "label-help-text", "help-text", "placeholder", "show-error", "error-message"]),
        _createVNode(EmailField, {
          modelValue: formData.email.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.email.value) = $event)),
          "show-error": formData.email.validity === _unref(Validity).INVALID,
          "is-max-width-field": true,
          onFieldChanged: _cache[7] || (_cache[7] = () => validateField( 'email' ))
        }, null, 8, ["modelValue", "show-error"]),
        _createVNode(SelectField, {
          modelValue: formData.topic.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.topic.value) = $event)),
          id: "contact-form-topic",
          "input-id": "topic",
          name: "category",
          label: _ctx.$t( 'contact_form_topic_label' ),
          options: [
						{ label: _ctx.$t( 'contact_form_topic_placeholder' ), value: '' },
						...Object.values( __props.contactCategories ).map( ( value ) => ( { label: value, value: value } ) )
					],
          "show-error": formData.topic.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_topic_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[9] || (_cache[9] = () => validateField( 'topic' ))
        }, null, 8, ["modelValue", "label", "options", "show-error", "error-message"]),
        _createVNode(TextField, {
          name: "subject",
          id: "contact-form-subject",
          "input-id": "subject",
          modelValue: formData.subject.value,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formData.subject.value) = $event)),
          label: _ctx.$t( 'contact_form_subject_label' ),
          placeholder: _ctx.$t( 'contact_form_subject_placeholder' ),
          "show-error": formData.subject.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_subject_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[11] || (_cache[11] = () => validateField( 'subject' ))
        }, null, 8, ["modelValue", "label", "placeholder", "show-error", "error-message"]),
        _createVNode(TextField, {
          name: "messageBody",
          "input-type": "textarea",
          id: "contact-form-messageBody",
          "input-id": "messageBody",
          modelValue: formData.comment.value,
          "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((formData.comment.value) = $event)),
          label: _ctx.$t( 'contact_form_body_label' ),
          placeholder: "",
          "show-error": formData.comment.validity === _unref(Validity).INVALID,
          "error-message": _ctx.$t( 'contact_form_body_error' ),
          "is-max-width-field": true,
          onFieldChanged: _cache[13] || (_cache[13] = () => validateField( 'comment' ))
        }, null, 8, ["modelValue", "label", "show-error", "error-message"]),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(FormButton, {
            id: "submit-btn",
            "button-type": "submit"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('contact_form_submit_button')), 1)
            ]),
            _: 1
          })
        ])
      ], 544)
    ]),
    _: 1
  }))
}
}

})